import { DataSourceItemType } from 'antd/lib/auto-complete'
import { SelectValue } from 'antd/lib/select'
import { debounce } from 'lodash'
import * as Raven from 'raven-js'
import * as React from 'react'
import { injectIntl } from 'react-intl'
import AutoComplete from 'web-makeup/dist/AutoComplete'
import Input from 'web-makeup/dist/Input'
import notification from 'web-makeup/dist/Notification'
import loadJS from 'scriptjs'

const Icon = require('antd/lib/icon')

interface OwnProps {
  apiKey: string
  language: string

  loadJS?: (key: string, cb: () => void) => void

  className?: string
  placeholder?: string

  onChange?: (value: SelectValue) => void
  onSelect?: (value: SelectValue, option: object) => void

  // Values to come from https://developers.google.com/places/supported_types#table3
  placeTypes?: string[]
}

type Props = OwnProps & ReactIntl.InjectedIntlProps

type State =
  | { screen: 'initializing' }
  | { screen: 'initialized' }
  | { screen: 'searching' }
  | {
      screen: 'success'
      dataSource: DataSourceItemType[]
    }
  | {
      screen: 'error'
      error: string
    }

export class LocationAutoComplete extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    loadJS,
  }

  places: google.maps.places.PlacesService
  search: google.maps.places.AutocompleteService

  constructor(props: Props) {
    super(props)

    this.state = { screen: 'initializing' }

    this.initialize()

    this.handleSearch = debounce(this.handleSearch.bind(this), 250)
    this.handleFetchSuccess = this.handleFetchSuccess.bind(this)
  }

  initialize(): Promise<State> {
    const { apiKey, language } = this.props

    const API_URL = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${language}`

    return new Promise(resolve => {
      if (this.props.loadJS) {
        this.props.loadJS(API_URL, () => {
          this.search = new google.maps.places.AutocompleteService()
          this.places = new google.maps.places.PlacesService(
            document.createElement('div')
          )

          this.setState({ screen: 'initialized' })

          resolve({ screen: 'initialized' })
        })
      }
    })
  }

  fetch(value: string): Promise<State> {
    return new Promise((resolve, reject) => {
      const { placeTypes } = this.props

      if (value.length === 0) {
        resolve({ screen: 'success', dataSource: [] })
        return
      }

      const params = {
        input: value,
        types: placeTypes ? placeTypes : undefined,
      }

      this.search.getPlacePredictions(
        params,
        this.handleFetchSuccess(resolve, reject)
      )
    })
  }

  handleFetchSuccess(resolve: (o) => void, reject: (o) => void) {
    return (predictions, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        resolve({
          screen: 'success',
          dataSource: predictions.map(prediction => ({
            value: prediction.place_id,
            text: prediction.description,
          })),
        })

        return
      }

      if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve({ screen: 'success', dataSource: [] })
        return
      }

      // gg
      reject({ screen: 'error', error: status })
    }
  }

  handleSearch(value: string) {
    const errorHandler = ({ screen, error }) => {
      const { formatMessage } = this.props.intl

      notification.error({
        message: formatMessage({
          id: 'location_autocomplete.error.message',
        }),
        description: formatMessage({
          id: 'location_autocomplete.error.try_again',
        }),
        duration: 0,
      })

      Raven.captureException(new Error(error.toString()))
    }

    this.setState({ screen: 'searching' })

    return this.fetch(value)
      .then(state => this.setState(state))
      .catch(errorHandler)
  }

  render() {
    const shouldShowSpinner =
      this.state.screen === 'initializing' || this.state.screen === 'searching'

    const { placeholder, className, ...props } = this.props

    return (
      <AutoComplete
        {...props}
        allowClear
        onSearch={this.handleSearch}
        disabled={this.state.screen === 'initializing'}
        dataSource={
          (this.state.screen === 'success' && this.state.dataSource) || []
        }
      >
        <Input
          data-qa="user-location"
          className={className}
          placeholder={
            this.state.screen === 'initialized'
              ? placeholder
              : this.props.intl.formatMessage({ id: 'loading' })
          }
          prefix={
            shouldShowSpinner ? (
              <Icon type="loading" style={{ color: '#d8d8d8' }} />
            ) : (
              <Icon type="environment" style={{ color: '#d8d8d8' }} />
            )
          }
        />
      </AutoComplete>
    )
  }
}

export default injectIntl(LocationAutoComplete)



// WEBPACK FOOTER //
// ./src/components/LocationAutoComplete/LocationAutoComplete.tsx